<template>
  <v-dialog
    :value="dialog"
    max-width="90rem"
    scrollable
    @click:outside="closeDialog()"
  >
    <v-card height="90vh">
      <div
        class="d-flex flex-row justify-content-between align-items-center w-100 px-1"
        style="gap: 1rem"
      >
        <v-tabs
          v-if="dialog"
          v-model="tabIndex"
          show-arrows
          class="flex-1 tw-min-w-0"
        >
          <v-tab>Search by identifiers</v-tab>
          <v-tab>Retrieve Comptox List {{ comptoxLists != null ? ` (${comptoxLists.length})` : '' }}</v-tab>
          <v-tab>Retrieve by GHS Codes {{ hcodesOptions != null ? ` (${hcodesOptions.length})` : '' }}</v-tab>
          <v-tab>Search by structure</v-tab>
          <v-tab>Search result</v-tab>
        </v-tabs>

        <v-btn
          icon
          @click="closeDialog()"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-tabs-items
        v-model="tabIndex"
        active-class="d-flex flex-column h-100 px-3 pb-1 pt-4"
        style="flex-grow: 1; min-height: 0"
        touchless
      >
        <!-- Identifiers -->
        <v-tab-item>
          <div
            class="d-flex flex-column"
            style="flex-grow: 1; min-height: 0; gap: 0.5rem"
          >
            <b-form-textarea
              id="textarea1"
              v-model="searchText"
              :max-rows="10"
              :rows="10"
              autofocus
              placeholder="Any combination of DTXSID, SMILES, Name, CASRN, InChI and InChIKey, one per line"
            />

            <div
              v-if="notResolvedIdsBySearchText?.length"
              class="d-flex flex-column overflow-auto"
              style="gap: 0.25rem; max-height: 5rem"
            >
              <div class="text-danger">Following identifiers were not resolved:</div>

              <div
                class="d-flex flex-row flex-wrap"
                style="gap: 0.25rem"
              >
                <b-badge
                  v-for="(item, i) in notResolvedIdsBySearchText"
                  :key="i"
                  style="max-width: 10rem"
                >
                  <div class="text-truncate">{{ item }}</div>
                </b-badge>

                <b-spinner
                  v-if="isSearchingBySearchText"
                  label="Spinning"
                  small
                />
              </div>
            </div>
          </div>

          <div class="border-top pt-2 pb-1 text-right">
            <v-btn
              :disabled="searchText.length === 0"
              @click="searchByIdentifiers()"
              aria-label="Search by identifiers"
            >
              Search
            </v-btn>
          </div>
        </v-tab-item>

        <!-- Comptox List -->
        <v-tab-item>
          <div style="flex-grow: 1; min-height: 0">
            <span class="lists-num">
              Number of CompTox Lists:
              <a
                target="_blank"
                href="https://comptox.epa.gov/dashboard/chemical-lists"
              >
                {{ comptoxLists?.length ?? 0 }}
              </a>
            </span>
            <b-form-input
              v-model="listFilter"
              type="text"
              class="mb-2"
              placeholder="Filter lists by name"
            />
            <b-form-select
              v-model="list"
              :options="listsOptions"
              :select-size="10"
              class="mb-4"
              style="height: 15rem; overflow: auto"
            />
            <div
              v-if="list"
              v-html="selectedList.longDescription"
              style="max-height: 10rem; overflow: auto"
            />
          </div>

          <div class="border-top pt-2 pb-1 text-right">
            <v-btn
              :disabled="!list"
              @click="retrieveList()"
              aria-label="Retrieve selected list"
            >
              Retrieve
            </v-btn>
          </div>
        </v-tab-item>

        <!-- GHS Codes are commented out as do not work -->
        <v-tab-item>
          <div style="flex-grow: 1; min-height: 0">
            <span class="lists-num">Number of GHS Codes Options: {{ hcodesOptions?.length ?? 0 }}</span>
            <b-form-input
              v-model="hcodeFilter"
              type="text"
              class="mb-2"
              placeholder="Filter statements by term"
            />

            <b-form-select
              v-model="hcode"
              variant="light"
              multiple
              :select-size="10"
              :options="hcodesOptions"
              style="height: 15rem; overflow: auto"
            />
          </div>

          <div class="border-top pt-2 pb-1 text-right">
            <v-btn
              :disabled="!hcode?.length"
              @click="retrieveList()"
              aria-label="Retrieve by GHS Codes"
            >
              Retrieve
            </v-btn>
          </div>
        </v-tab-item>

        <!-- Structure -->
        <v-tab-item>
          <div style="flex-grow: 1; min-height: 0">
            <KetcherEditor
              v-model="mol"
              resolve
            />
          </div>

          <div class="px-2">
            <div
              class="d-flex flex-column"
              style="gap: 1rem"
            >
              <div class="d-flex flex-row">
                <b-form-radio-group
                  v-model="sssType"
                  :options="sssTypes"
                />

                <b-form-input
                  id="min-similarity"
                  v-if="sssType === 'SUBSTRUCTURE' || sssType === 'SIMILAR'"
                  v-model="minSimilarity"
                  max="1"
                  min="0.2"
                  step="0.01"
                  type="range"
                  aria-label="Minimum similarity"
                  style="max-width: 280px"
                />

                <output
                  for="min-similarity"
                  v-if="sssType === 'SUBSTRUCTURE' || sssType === 'SIMILAR'"
                  class="ml-2"
                >
                  {{ minSimilarity }}
                </output>
              </div>

              <div class="d-flex flex-row justify-content-between align-items-start">
                <div
                  class="d-flex flex-row"
                  style="gap: 1rem"
                >
                  <v-select
                    v-model="toxicity"
                    :items="toxicities"
                    label="Minimum toxicity"
                    outlined
                    dense
                    class="col-2"
                  />

                  <v-select
                    v-model="authority"
                    :items="authorities"
                    label="Minimum authority"
                    outlined
                    dense
                    class="col-2"
                  />

                  <template v-if="sssType === 'SUBSTRUCTURE' || sssType === 'SIMILAR'">
                    <v-tooltip
                      top
                      v-bind="defaultVTooltipProps"
                    >
                      <template #activator="{ on }">
                        <div
                          class="col-2 p-0"
                          v-on="on"
                        >
                          <v-text-field
                            v-model="sssMin"
                            label="Min. No. of Structures"
                            type="number"
                            min="1"
                            dense
                            outlined
                            hide-details
                            @change="sssMin = sssMin > 0 ? Math.abs(sssMin) : null"
                          />
                        </div>
                      </template>
                      Minimum number of structures
                    </v-tooltip>
                  </template>

                  <template>
                    <v-tooltip
                      top
                      v-bind="defaultVTooltipProps"
                    >
                      <template #activator="{ on }">
                        <div
                          class="col-2 p-0"
                          v-on="on"
                        >
                          <v-text-field
                            v-model="sssLimit"
                            label="Max. No. of Structures"
                            type="number"
                            min="1"
                            dense
                            outlined
                            hide-details
                            @change="sssLimit = sssLimit > 0 ? Math.abs(sssLimit) : null"
                          />
                        </div>
                      </template>
                      Maximum number of structures
                    </v-tooltip>
                  </template>
                </div>

                <b-button
                  variant="primary"
                  @click="structureSearch()"
                >
                  Search
                </b-button>
              </div>
            </div>
          </div>
        </v-tab-item>

        <!-- Result -->
        <v-tab-item>
          <div
            v-if="searching"
            class="loading-result"
          >
            <v-icon
              class="mdi-spin"
              large
            >
              mdi-rotate-right
            </v-icon>
          </div>

          <div
            v-if="!searching && found.length === 0"
            class="loading-result"
          >
            <p v-if="!searchError">No result</p>
            <p v-else>
              {{ searchError }}
            </p>
          </div>

          <div
            v-if="found.length > 0"
            class="d-flex flex-column h-100"
          >
            <div class="d-flex flex-column">
              <div
                class="d-flex w-100 flex-row align-items-center justify-content-between flex-wrap"
                style="gap: 1rem"
              >
                <div
                  class="d-flex flex-row align-items-center"
                  style="gap: 0.5rem"
                >
                  <v-btn
                    v-bind="defaultDenseButtonProps"
                    :disabled="selectedChemicals.length === 0"
                    id="lookup-add-to-cart-btn"
                    @click="onAdd()"
                    aria-label="Add to Cart"
                    color="primary"
                  >
                    <v-icon
                      left
                      class="text-white"
                    >
                      mdi-cart-plus
                    </v-icon>
                    <span>Add to Cart</span>
                  </v-btn>

                  <v-tooltip
                    top
                    v-bind="defaultVTooltipProps"
                    activator="#lookup-add-to-cart-btn"
                  >
                    Add chemicals to the cart
                  </v-tooltip>

                  <v-btn
                    v-bind="defaultDenseButtonProps"
                    id="lookup-remove-all-btn"
                    @click="removeAll"
                    aria-label="Clear search results"
                  >
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                  <v-tooltip
                    top
                    v-bind="defaultVTooltipProps"
                    activator="#lookup-remove-all-btn"
                  >
                    Clear search results
                  </v-tooltip>
                </div>

                <template v-if="searchType === 'ID'">
                  Chemical(s) found by {{ searchIds.length }} identifier(s)
                </template>
                <template v-else-if="searchType === 'SIM' && searchIds.length > 0">
                  Chemical(s) found by {{ searchIds.length }} identifier(s), ordered by similarity
                </template>
                <template v-else-if="searchType === 'SIM'">Chemical(s) ordered by similarity</template>
                <template v-else-if="searchType === 'SSS'">Found by {{ sssType }}</template>

                <b-form-input
                  v-if="searchType === 'SSS' || searchType === 'SIM'"
                  v-model="cutoffSimilarity"
                  :disabled="minSim === maxSim"
                  min="0"
                  step="0.1"
                  max="1"
                  type="range"
                  style="width: 10em"
                />
                <span
                  class="ml-3"
                  v-if="searchType === 'SSS' || searchType === 'SIM'"
                >
                  {{ cutoffSimilarity }}
                </span>

                <v-spacer />

                <div
                  class="d-flex flex-row align-items-center"
                  style="gap: 0.25rem"
                >
                  <div class="d-flex flex-row align-items-center">
                    <v-chip
                      id="lookup-filtered-chip"
                      small
                      class="mr-0"
                    >
                      {{ selectedChemicals.length }}
                    </v-chip>
                    <span>/</span>
                    <v-chip
                      id="lookup-total-chip"
                      small
                    >
                      {{ found.length }}
                    </v-chip>
                  </div>

                  <v-tooltip
                    top
                    v-bind="defaultVTooltipProps"
                    activator="#lookup-filtered-chip"
                  >
                    Filtered
                  </v-tooltip>
                  <v-tooltip
                    top
                    v-bind="defaultVTooltipProps"
                    activator="#lookup-total-chip"
                  >
                    Total
                  </v-tooltip>

                  <v-btn-toggle dense>
                    <v-btn
                      id="lookup-select-all-btn"
                      @click="selectAll()"
                      aria-label="Select All"
                    >
                      <v-icon>mdi-checkbox-multiple-marked-outline</v-icon>
                    </v-btn>
                    <v-tooltip
                      top
                      v-bind="defaultVTooltipProps"
                      activator="#lookup-select-all-btn"
                    >
                      Select All
                    </v-tooltip>

                    <v-btn
                      id="lookup-select-none-btn"
                      @click="selectNone()"
                      aria-label="Select None"
                    >
                      <v-icon>mdi-checkbox-multiple-blank-outline</v-icon>
                    </v-btn>
                    <v-tooltip
                      top
                      v-bind="defaultVTooltipProps"
                      activator="#lookup-select-none-btn"
                    >
                      Select None
                    </v-tooltip>
                  </v-btn-toggle>
                </div>

                <OptionsDropdown
                  :items="lookupExportItems"
                  title="Export to SDF, Excel, Word or PDF"
                />
              </div>

              <v-progress-linear
                v-if="downloading"
                indeterminate
              />
            </div>

            <div
              class="overflow-auto"
              style="flex-grow: 1; min-height: 0"
            >
              <div
                class="d-flex flex-row mt-4 flex-wrap"
                style="gap: 0.5rem"
              >
                <ChemCard
                  v-for="(item, index) in pagedStructures"
                  :key="index"
                  :item="item"
                  show-props-popup
                  show-check
                  show-order
                  @check-click="invertSelection"
                  @order="onOrderSimilarity"
                />
              </div>
            </div>

            <div
              class="d-flex flex-row flex-wrap align-items-center justify-content-between"
              style="gap: 1rem"
            >
              <BistateFilter
                v-model="filters"
                size="sm"
              />

              <div
                v-if="filteredFound && this.sssMin > 0 && this.appliedMin != null"
                class="applied-min-message"
              >
                <v-icon style="color: #ffc107">mdi-alert-circle-outline</v-icon>
                The min. similarity parameter was reduced to
                <span class="badge badge-warning">
                  {{ this.appliedMin.toFixed(3) }}
                </span>
                to find the specified minimum number of records
                <span class="badge badge-secondary">({{ this.sssMin }})</span>
              </div>

              <div v-if="pageSize < filteredFound.length">
                <b-pagination
                  v-model="currentPage"
                  :limit="5"
                  :per-page="pageSize"
                  :total-rows="filteredFound.length"
                  size="md"
                />
              </div>
            </div>
          </div>
        </v-tab-item>
      </v-tabs-items>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  download,
  eventBus,
  getDatedFilename,
  getErrorMessage,
  isValidChemical,
  RESOLVER_URL,
  SEARCH_URL,
} from '@/main'
import { mapGetters } from 'vuex'
import KetcherEditor from '@/components/ketcher-editor/KetcherEditor.vue'
import ChemCard from '@/components/chemical/ChemCard.vue'
import LookupFilterMixin from '@/components/lookup/LookupFilterMixin.vue'
import BistateFilter from '@/components/BistateFilter.vue'
import { defaultVTooltipProps, defaultDenseButtonProps } from '@/utils/defaultProps'
import { debounce } from 'lodash-es'
import OptionsDropdown from '@/components/OptionsDropdown.vue'
import { showErrorToast } from '@/utils/toast'

export default {
  mixins: [LookupFilterMixin],
  components: { KetcherEditor, ChemCard, BistateFilter, OptionsDropdown },

  data() {
    return {
      // dialog: true,
      tabIndex: 0,
      searchText: '',
      searchIds: [],
      searchType: null,
      mol: null,
      list: null,
      listFilter: null,
      hcode: [],
      hcodeFilter: null,

      sssType: 'SUBSTRUCTURE',
      sssTypes: [
        { text: 'Substructure', value: 'SUBSTRUCTURE' },
        { text: 'Similarity', value: 'SIMILAR' },
      ],
      minSimilarity: 0.85,
      appliedMin: null,
      sssLimit: 20,
      sssMin: null,
      cutoffSimilarity: null,

      toxicity: null,
      authority: null,

      toxicities: [
        { text: 'Very High', value: 'VH' },
        { text: 'High', value: 'H' },
        { text: 'Medium', value: 'M' },
        { text: 'Low', value: 'L' },
        { text: 'Any', value: 'A' },
        { text: '', value: null },
      ],
      authorities: [
        { text: 'Authoritative', value: 'Authoritative' },
        { text: 'Screening', value: 'Screening' },
        { text: 'QSAR Model', value: 'QSAR Model' },
        { text: '', value: null },
      ],

      downloading: false,
      searching: false,
      searchError: null,
      isSearchingBySearchText: false,
      resolvedBySearchText: [],
      found: [],

      currentPage: 1,
      pageSize: 20,
      selectedForSimilarity: null,
      defaultVTooltipProps,
      defaultDenseButtonProps,
    }
  },

  props: {
    dialog: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    ...mapGetters(['comptoxLists', 'hcodes']),
    minSim: function () {
      return this.found
        .filter((str) => str.chemical && str.chemical.similarity)
        .reduce((min, str) => Math.min(min, str.chemical.similarity), 1)
    },
    maxSim: function () {
      return this.found
        .filter((str) => str.chemical && str.chemical.similarity)
        .reduce((min, str) => Math.max(min, str.chemical.similarity), 0)
    },
    cutoffStep: function () {
      return Math.abs(1 - this.minSim) / 10
    },
    RESOLVER_URL() {
      return RESOLVER_URL
    },
    listsOptions() {
      return (
        this.comptoxLists == null
          ? []
          : this.listFilter
            ? this.comptoxLists.filter(
                (l) =>
                  l.listName.toLowerCase().includes(this.listFilter.toLowerCase()) ||
                  l.shortDescription.toLowerCase().includes(this.listFilter.toLowerCase()),
              )
            : this.comptoxLists
      ).map((l) => ({
        value: l.listName,
        html: `<b>${l.listName}</b> (${l.chemicalCount}): ${l.shortDescription}`,
      }))
    },
    selectedList() {
      return this.comptoxLists.find((l) => l.listName === this.list)
    },
    hcodesOptions() {
      return (
        this.hcodes == null
          ? []
          : this.hcodeFilter
            ? this.hcodes.filter(
                (l) =>
                  l.statement.toLowerCase().includes(this.hcodeFilter.toLowerCase()) ||
                  l.codes.some((code) => code.toLowerCase().includes(this.hcodeFilter.toLowerCase())),
              )
            : this.hcodes
      ).map((l) => ({
        value: l.codes,
        text: `${l.codes} (${l.count}): ${l.statement}`,
      }))
    },
    selectedChemicals() {
      return this.found.filter((s) => s.checked)
    },
    pagedStructures() {
      return this.filteredFound.slice((this.currentPage - 1) * this.pageSize, this.currentPage * this.pageSize)
    },

    notResolvedIdsBySearchText() {
      return [...new Set(this.resolvedBySearchText.filter((result) => !result.chemical).map((result) => result.query))]
    },
    lookupExportItems() {
      return [
        { title: 'Export to SDF', icon: 'mdi-file-download-outline', onClick: () => this.onExport('SDF') },
        { title: 'Export to CSV', icon: 'mdi-file-delimited-outline', onClick: () => this.onExport('CSV') },
        { title: 'Export to XLSX', icon: 'mdi-file-excel-outline', onClick: () => this.onExport('XLSX') },
        { title: 'Export to SMI', icon: 'mdi-file-document-outline', onClick: () => this.onExport('SMI') },
      ]
    },
  },

  watch: {
    // dialog(val) {
    //   if (!val) this.$emit('close')
    // },

    cutoffSimilarity: function (newValue) {
      if (this.found) this.found.forEach((s) => (s.checked = s.chemical.similarity >= newValue))
    },

    searchText(val) {
      this.lookup(val)
    },
  },

  methods: {
    closeDialog() {
      this.tabIndex = 0
      this.$emit('close')
    },

    lookup: debounce(function (idsText) {
      this.isSearchingBySearchText = true
      const ids = idsText.split(/\r?\n/).filter((s) => !s.match(/^(,|;|\s*)$/))
      // this.searching = true
      // this.message = null
      return this.$axios
        .post(`${RESOLVER_URL}/lookup`, { ids: ids })
        .then((response) => {
          // this.searching = false
          this.resolvedBySearchText = [...response.data]
        })
        .catch((error) => {
          // this.searching = false
          // this.message = getErrorMessage(error)
        })
        .finally(() => {
          this.isSearchingBySearchText = false
        })
    }, 1000),

    showSearchModal() {
      this.tabIndex = 0
      this.searchText = ''
    },
    // hideSearchModal() {
    //   this.dialog = false
    // },
    searchByIdentifiers() {
      this.searchIds = this.searchText
        .split('\n')
        .map((s) => s.trim())
        .filter((str) => !!str)
      if (!this.searchIds) return

      this.searchType = 'ID'
      this.searching = true
      this.searchError = null
      this.found = []
      this.currentPage = 1

      this.$axios
        .post(`${RESOLVER_URL}/lookup`, { ids: this.searchIds })
        .then((response) => {
          response.data.forEach((item) => {
            if (isValidChemical(item)) item.checked = true
          })
          this.found = response.data
        })
        .catch((error) => {
          showErrorToast(error, { title: 'Error during search' })
        })
        .finally(() => {
          this.searching = false
        })

      this.tabIndex = 4
    },
    retrieveList() {
      this.searchType = 'List'
      this.searching = true
      this.searchError = null
      this.found = []
      this.currentPage = 1
      let url
      if (this.list && this.tabIndex === 1) {
        url = `${RESOLVER_URL}/ccte-list?name=${encodeURIComponent(this.list)}`
      } else if (this.hcode?.length && this.tabIndex === 2) {
        url = `${RESOLVER_URL}/ghs-list?name=${encodeURIComponent(this.hcode)}`
      }
      this.$axios
        .get(url)
        .then((response) => {
          response.data.forEach((item) => {
            if (isValidChemical(item)) item.checked = true
          })
          this.searching = false
          this.found = response.data
        })
        .catch((error) => {
          this.searching = false
          this.searchError = getErrorMessage(error)
        })

      this.tabIndex = 4
    },
    structureSearch() {
      this.searchType = 'SSS'
      this.searching = true
      this.found = []
      this.currentPage = 1

      const queryBody = {
        searchType: this.sssType,
        inputType: 'SMILES',
        query: this.mol,
        maxNumber: this.sssLimit,
        minNumber: this.sssMin,
        params: {
          'min-similarity': this.minSimilarity,
          'similarity-type': 'tanimoto',
          'filter-stereo': null,
          'filter-chiral': null,
          'filter-isotopes': false,
          'filter-charged': false,
          'filter-multicomponent': false,
          'filter-radicals': false,
          'filter-salts': null,
          'filter-polymers': false,
          'filter-sgroups': false,
          'filter-toxicity': null,
          'min-toxicity': this.toxicity,
          'min-authority': this.authority,
        },
      }

      this.$axios
        .post(`${SEARCH_URL}`, queryBody, { timeout: 60 * 1000 })
        .then((searchResponse) => {
          const records = searchResponse.data.records
          this.appliedMin = searchResponse.data.appliedMin
          if (!records?.length) {
            this.found = []
            return
          }
          const similarityBySid = Object.fromEntries(records.map((s) => [s.sid, s.similarity]))
          return this.$axios
            .post(`${RESOLVER_URL}/lookup`, { ids: records.map((s) => s.sid) })
            .then((lookupResponse) => {
              lookupResponse.data.forEach((str) => {
                if (str.chemical) str.chemical.similarity = similarityBySid[str.query]
                str.checked = true
              })
              this.found = lookupResponse.data
            })
        })
        .catch((error) => {
          showErrorToast(error, { title: 'Error during structure search' })
        })
        .finally(() => {
          this.searching = false
        })

      // this.$axios
      //   .post(`${SEARCH_URL}`, queryBody, { timeout: 60 * 1000 })
      //   .then((searchResponse) => {
      //     const records = searchResponse.data.records
      //     this.appliedMin = searchResponse.data.appliedMin
      //     const sss = records.slice(0)
      //     console.log('sss', sss)
      //     this.$axios
      //       .post(`${RESOLVER_URL}/lookup`, { ids: sss.map((s) => s.sid) })
      //       .then((response) => {
      //         response.data.forEach((str) => {
      //           if (str.chemical) str.chemical.similarity = sss.find((s) => s.sid === str.query).similarity
      //           str.checked = true
      //         })
      //         this.found = response.data
      //       })
      //       .catch((error) => {
      //         showErrorToast(error, { title: 'Error during structure search' })
      //       })
      //       .finally(() => {
      //         this.searching = false
      //       })
      //   })
      //   .catch((error) => {
      //     showErrorToast(error, { title: 'Error during structure search' })
      //   })
      //   .finally(() => {
      //     this.searching = false
      //   })

      this.tabIndex = 4
    },
    onAdd() {
      //this.selectedChemicals.forEach((s) => (delete s.chemical.similarity));
      this.$store.dispatch('addUniqueStructures', this.selectedChemicals)
      this.$store.commit('SET_VIEW_STRUCTURES', true)
      this.closeDialog()
    },
    selectAll() {
      this.found.map((s) => {
        if (isValidChemical(s)) s.checked = true
        return s
      })
    },
    selectNone() {
      this.found.map((s) => {
        if (isValidChemical(s)) s.checked = false
        return s
      })
    },
    invertSelection(structure) {
      const updateObj = this.found.find((e) => e.chemical.chemId === structure.chemId)

      if (updateObj) updateObj.checked = !updateObj.checked
    },
    onOrderSimilarity(structure) {
      console.log('Ordering by similarity to', structure)
      this.searchType = 'SIM'
      this.searching = true
      this.searchError = null
      this.currentPage = 1
      this.cutoffSimilarity = 0
      this.selectedForSimilarity = structure
      this.$axios
        .post(`${RESOLVER_URL}/orderBySimilarity?`, {
          main: structure,
          chemicals: this.selectedChemicals,
        })
        .then((response) => {
          response.data.forEach((str) => {
            if (str.chemical) str.chemical.similarity = str.similarity
            str.checked = true
          })
          this.found = response.data
        })
        .catch((error) => {
          showErrorToast(error, { title: 'Error during order by similarity' })
        })
        .finally(() => {
          this.searching = false
        })
    },
    removeAll() {
      this.found = []
      this.currentPage = 1
    },
    onExport(format) {
      this.downloading = true
      let filters = {}
      for (let f of this.filters) filters[f.value] = f.state
      this.$axios
        .post(
          `${RESOLVER_URL}/export-records`,
          {
            records: this.filteredFound,
            format: format,
            selectedForSimilarity: this.searchType === 'SIM' ? this.selectedForSimilarity : null,
          },
          { responseType: 'blob' },
        )
        .then((response) => {
          let filename = getDatedFilename('resolver', format.toLowerCase())
          download(filename, response.data, format.toLowerCase())
        })
        .catch((error) => {
          showErrorToast(error, { title: 'Error during export' })
        })
        .finally(() => {
          this.downloading = false
        })
    },
  },

  created() {
    eventBus.$on('START_SEARCH', () => this.showSearchModal())
  },
}
</script>

<style lang="scss" scoped>
.loading-result {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
}

.applied-min-message {
  margin-top: 1rem;
  margin-left: -0.9rem;
}

.lists-num {
  font-weight: bolder;
  color: #8b898b;
}

::v-deep .v-window__container {
  height: 100%;
}
</style>
